import React from 'react';
import { Box, Card, CardContent, Chip, Skeleton, Stack, Typography } from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { format, fromUnixTime } from 'date-fns';

export function fmtStripeMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function fmtStripeUnix(ts: number): string {
  try {
    return format(fromUnixTime(ts), 'PPp');
  } catch {
    return String(ts);
  }
}

export function subStatusColor(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'trialing') {
    return 'info';
  }
  if (status === 'past_due' || status === 'unpaid') {
    return 'error';
  }
  if (status === 'canceled') {
    return 'default';
  }
  return 'warning';
}

export function invoiceStatusColor(status: string | null | undefined): 'default' | 'success' | 'warning' | 'error' | 'info' {
  const s = status || '';
  if (s === 'paid') {
    return 'success';
  }
  if (s === 'open') {
    return 'warning';
  }
  if (s === 'draft') {
    return 'default';
  }
  if (s === 'void' || s === 'uncollectible') {
    return 'error';
  }
  return 'info';
}

export function payoutStatusColor(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (status === 'paid') {
    return 'success';
  }
  if (status === 'failed') {
    return 'error';
  }
  if (status === 'pending' || status === 'in_transit') {
    return 'warning';
  }
  return 'default';
}

export function StripeHero({ compact, title, subtitle }: { compact?: boolean; title?: string; subtitle?: string }) {
  return (
    <Box sx={compact ? undefined : { mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={compact ? 0 : 0.5}>
        <CreditCardOutlinedIcon sx={{ fontSize: compact ? 28 : 32, color: '#635bff' }} />
        <Typography variant={compact ? 'h5' : 'h4'} fontWeight={800}>
          {title || 'Stripe'}
        </Typography>
      </Stack>
      {!compact && subtitle && (
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
          {subtitle}
        </Typography>
      )}
      {compact && subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export function StripeStatCard({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        </Box>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}22`, color }}>{icon}</Box>
      </Stack>
    </Box>
  );
}

export function StripeKpiCard({
  icon,
  title,
  value,
  hint,
  loading,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  hint?: string;
  loading: boolean;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {icon}
          <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.5}>
            {title.toUpperCase()}
          </Typography>
        </Stack>
        {loading ? (
          <Skeleton width="60%" height={36} />
        ) : (
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {value}
          </Typography>
        )}
        {hint && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function StripeStatusChip({
  label,
  color,
}: {
  label: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
}) {
  return <Chip size="small" label={label} color={color} />;
}
